import React, { createContext, useContext, useState, useEffect } from 'react';
import { api } from '../utils/api';

const AuthContext = createContext(null);

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(null);
  const [loading, setLoading] = useState(true);

  // Fetch /api/auth/me and merge role/scope/status into the user object.
  // Best-effort: failure (e.g. legacy session without users-collection row)
  // leaves user as-is so existing flows keep working.
  const refreshMe = async () => {
    try {
      const resp = await api.get('/auth/me');
      setUser((prev) => ({ ...(prev || {}), ...resp.data }));
      // Persist enriched user so reloads preserve role/scope until next refresh
      const stored = localStorage.getItem('qoldo_user');
      const merged = { ...(stored ? JSON.parse(stored) : {}), ...resp.data };
      localStorage.setItem('qoldo_user', JSON.stringify(merged));
      return resp.data;
    } catch (e) {
      // Silent — Phase 1 already handles legacy users gracefully on the backend
      return null;
    }
  };

  useEffect(() => {
    // Check for stored token
    const storedToken = localStorage.getItem('qoldo_token');
    const storedUser = localStorage.getItem('qoldo_user');
    
    if (storedToken && storedUser) {
      setToken(storedToken);
      setUser(JSON.parse(storedUser));
      // Refresh role/scope from backend in the background
      refreshMe().finally(() => setLoading(false));
      return;
    }
    setLoading(false);
  }, []);

  const login = (userData, authToken) => {
    setUser(userData);
    setToken(authToken);
    localStorage.setItem('qoldo_token', authToken);
    localStorage.setItem('qoldo_user', JSON.stringify(userData));
    // Fire-and-forget enrichment with role/scope from /auth/me
    refreshMe();
  };

  const logout = () => {
    setUser(null);
    setToken(null);
    localStorage.removeItem('qoldo_token');
    localStorage.removeItem('qoldo_user');
  };

  const isSuperAdmin = !!user && user.role === 'super_admin';

  return (
    <AuthContext.Provider value={{ user, token, login, logout, loading, isSuperAdmin, refreshMe }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};